import * as React from "react";
import { useCallback } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles"; // Updated import here

import { Button } from "react-admin";
import { useTranslate } from "ra-core";

import { CancelIcon, OKIcon } from "./iconography";

import classnames from "classnames";

const useStyles = makeStyles(
  (theme) => ({
    confirmPrimary: {
      color: theme.palette.primary.main,
    },
    confirmWarning: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaConfirm" },
);

/**
 * Confirmation dialog
 *
 * @example
 * <Confirm
 *     isOpen={true}
 *     title="Delete Item"
 *     content="Are you sure you want to delete this item?"
 *     confirm="Yes"
 *     confirmColor="primary"
 *     ConfirmIcon=ActionCheck
 *     CancelIcon=AlertError
 *     cancel="Cancel"
 *     onConfirm={() => { // if null do nothing else do something }}
 *     onClose={() => { // do something }}
 * />
 */
export const ConditionalConfirm = (props) => {
  const {
    cancel = "ra.action.cancel",
    confirm = "ra.action.confirm",
    confirmColor = "primary",
    ConfirmIcon = OKIcon,
    CloseIcon = CancelIcon,
    isOpen = false,
    loading,
    title,
    content,
    onClose,
    onConfirm,
    translateOptions = {},
  } = props;
  const classes = useStyles(props);
  const translate = useTranslate();

  const handleConfirm = useCallback(
    (e) => {
      e.stopPropagation();
      onConfirm(e);
    },
    [onConfirm],
  );

  const handleClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onClick={handleClick}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">
        {translate(title, { _: title, ...translateOptions })}
      </DialogTitle>
      <DialogContent>
        {typeof content === "string" ? (
          <DialogContentText>
            {translate(content, {
              _: content,
              ...translateOptions,
            })}
          </DialogContentText>
        ) : (
          content
        )}
      </DialogContent>
      <DialogActions>
        <Button
          label={translate(cancel, { _: cancel })}
          color="default"
          disabled={loading}
          onClick={onClose}
        >
          <CloseIcon />
        </Button>
        {onConfirm && (
          <Button
            label={translate(confirm, { _: confirm })}
            disabled={loading}
            onClick={handleConfirm}
            className={classnames("ra-confirm", {
              [classes.confirmWarning]: confirmColor === "warning",
              [classes.confirmPrimary]: confirmColor === "primary",
            })}
            autoFocus
          >
            <ConfirmIcon />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
